import { Component, ElementRef, HostBinding, OnInit, OnDestroy } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { CheckoutService } from '../checkout.service';
import { CheckoutBasketModel } from '../models/basket.model';
import { UIRouter } from '@uirouter/core';
import { AppState } from '../../app.service';
import { AuthService } from '../../auth/auth.service';
import { Subscription } from 'rxjs';
import { UserModel } from '../../auth/models/user.model';
import { ReceiptDataModel } from '../models/receiptData.model';

@Component({
  templateUrl: './register.component.html',
  styleUrls: [
    './register.component.scss'
  ]
})
export class CheckoutRegisterComponent implements OnInit, OnDestroy {
  public isPhysicalButton: boolean = false;
  public currentUserSub: Subscription;

  public user: UserModel = new UserModel();
  public receiptData: ReceiptDataModel = new ReceiptDataModel();
  public userFirstName: string = '';
  public userLastName: string = '';

  public isLoggedIn: boolean = false;
  public canOrderReceipt: boolean = false;
  public donationReceipt: boolean = false;
  public userExists: boolean = false;
  public isVoucher: boolean = false;

  public basket: CheckoutBasketModel;

  public displayNameOptions: {anonymous: string, name?: string} = {
    anonymous: 'Anonymer Spender'
  };

  private presenteeFirstName: string = '';
  private presenteeLastName: string = '';
  public presenteeEmail: string = '';
  private uniquePresentee: boolean = true;

  constructor(
    private authService: AuthService,
    private checkoutService: CheckoutService,
    private router: UIRouter,
    private appState: AppState,
    private elementRef: ElementRef
  ) {
  }

  public ngOnInit() {
    this.isLoggedIn = !!this.authService.currentUser;

    if (this.isLoggedIn) {
      this.checkoutService.setUser(this.authService.currentUser);
      this.userFirstName = this.checkoutService.user.firstName;
      this.userLastName = this.checkoutService.user.lastName;
    } else {
      this.userFirstName = '';
      this.userLastName = '';
    }

    this.currentUserSub = this.authService.currentUserChange.subscribe(
      (result) => {
        this.isLoggedIn = !!result;

        this.checkoutService.setUser(this.isLoggedIn ? result : null);

        if (this.isLoggedIn) {
          this.userFirstName = this.checkoutService.user.firstName;
          this.userLastName = this.checkoutService.user.lastName;
          this.appState.set('isMenuActive', false);
        } else {
          this.userFirstName = '';
          this.userLastName = '';
        }
      }
    );

    this.basket = this.checkoutService.basket;

    if (this.basket.netTotal >= 200.0) {
      this.canOrderReceipt = true;
    }

    if (!this.checkoutService.items || !this.checkoutService.items.length) {
      this.router.stateService.go('main.checkout.index');
    }

    if (!this.isLoggedIn) {
      this.checkoutService.items.forEach(item => {
        if (item.voucher) {
          this.isVoucher = true;

          if (item.voucher.button.presentee) {
            if (this.presenteeFirstName === '' && this.presenteeLastName === '' && this.presenteeEmail === '') {
              this.presenteeFirstName = item.voucher.button.presentee.firstName;
              this.presenteeLastName = item.voucher.button.presentee.lastName;
              this.presenteeEmail= item.voucher.button.presentee.email;
            } else {
              if (this.presenteeFirstName !== item.voucher.button.presentee.firstName || 
                this.presenteeLastName !== item.voucher.button.presentee.lastName || 
                this.presenteeEmail !== item.voucher.button.presentee.email) {
                this.uniquePresentee = false;
              }
            }
          }
        }
      });

      if (this.presenteeFirstName !== '' && this.presenteeLastName !== '' &&
          this.presenteeEmail !== '' && this.uniquePresentee) {
        this.user.firstName = this.presenteeFirstName;
        this.user.lastName = this.presenteeLastName;
        this.user.email = this.presenteeEmail;
        this.user.emailRepeat = this.presenteeEmail;
        this.compileCreditName();
      }
    }
  }

  public ngOnDestroy() {
    this.currentUserSub.unsubscribe();
  }

  public handleSubmit(form: NgForm) {
    Object.keys(form.controls).forEach(key => {
      form.controls[key].updateValueAndValidity();
    });
    
    if (form.valid) {
      this.userExists = false;

      const date = new Date();
      let day = date.getDate().toString();
      let month = (date.getMonth() + 1).toString();

      if (day.length === 1) {
        day = '0' + day;
      }

      if (month.length === 1) {
        month = '0' + month;
      }

      const formattedDate = `${day}.${month}.${date.getFullYear()}`;

      if (this.user.email && !this.isLoggedIn) {
        this.user.email = this.user.email.toLowerCase();

        this.authService.userExists(this.user.email)
          .subscribe(
            (result) => {
              if (result.exists) {
                this.userExists = true;
              } else {
                this.authService.resetPrefillEmail();
                this.checkoutService.setUser(this.user);
                this.checkoutService.receiptData = null;

                if (this.donationReceipt) {
                  this.receiptData.worth = this.basket.netTotal;
                  this.receiptData.worthDate = formattedDate;
                  this.checkoutService.receiptData = this.receiptData;
                }

                this.router.stateService.go('main.checkout.payment');
              }
            }
          );
      } else if (this.isLoggedIn) {
        this.checkoutService.receiptData = null;

        if (this.donationReceipt) {
          this.receiptData.worth = this.basket.netTotal;
          this.receiptData.worthDate = formattedDate;
          this.checkoutService.receiptData = this.receiptData;
        }

        this.router.stateService.go('main.checkout.payment');
      }
    } else {
      let firstInvalidControl = this.elementRef.nativeElement.querySelectorAll('.ng-invalid');

      if (firstInvalidControl.length > 0) {
        if (typeof firstInvalidControl[1] !== 'undefined' && firstInvalidControl[1] !== null) {
          firstInvalidControl[1].scrollIntoView(true);
        } else {
          firstInvalidControl[0].scrollIntoView(true);
        }
      }
    }
  }

  public hasError(form: NgForm, field: any, type: string = 'required', excludeIf: string[]) {
    let hasError = field.invalid &&
      form.submitted &&
      field.errors &&
      field.errors[type];

    if (excludeIf) {
      for (const exclude of excludeIf) {
        if (field.errors && field.errors[exclude]) {
          hasError = false;
          break;
        }
      }
    }

    return hasError;
  }

  public compileCreditName() {
    if (this.user.firstName && this.user.lastName) {
      this.user.creditName = this.user.firstName + ' ' + this.user.lastName.charAt(0) + '.';

      this.displayNameOptions = {
        name: this.user.creditName,
        anonymous: 'Anonymer Spender'
      };

      this.user.options.displayName = 'name';
    } else {
      this.user.creditName = '';

      if (this.displayNameOptions.name) {
        this.displayNameOptions = {
          anonymous: 'Anonymer Spender'
        };

        this.user.options.displayName = 'anonymous';
      }
    }
  }

  public activateLogin() {
    this.appState.set('isMenuActive', true);
  }

  public activateLoginByLink(evt: any, prefillEmail?: boolean) {
    evt.preventDefault();

    if (prefillEmail === true) {
      this.authService.setPrefillEmail(this.user.email);
    } else {
      this.authService.resetPrefillEmail();
    }

    this.appState.set('isMenuActive', true);
  }

  public doLogout(evt) {
    evt.preventDefault();
    this.authService.logout();
  }

  public handleDonationReceipt(event: boolean) {
    this.donationReceipt = event;
  }
}


// WEBPACK FOOTER //
// ./src/app/checkout/register/register.component.ts